/**
 * InstantProtectedRoute - Robust protected route component
 * 
 * Features:
 * - Deterministic auth status handling with timeout
 * - Immediate redirect for unauthenticated users
 * - Retry logic with session refresh on timeout
 * - Optimistic UI with cached data
 * - Minimal skeleton instead of full-page spinner
 * - Prevents infinite loading loops
 * 
 * Changes from previous version:
 * - Uses authStatus enum instead of loading/authReady flags
 * - Implements 1.5s timeout with recovery attempt
 * - Uses safeRedirectToLogin with from state preservation
 * - Shows compact skeleton instead of full-page spinner
 */

import React, { useEffect, useRef, useState } from 'react';
import { useAuth, AuthStatus } from '../contexts/AuthContext';
import { safeRedirectToLogin, getCachedUserMeta } from '../utils/authHelpers';
import { useGlobalLoadingControl } from '../utils/loadingManager';

interface InstantProtectedRouteProps {
  children: React.ReactNode;
  /** Set to true if this route requires admin role */
  requireAdmin?: boolean;
}

/**
 * Protected route that requires authentication
 * 
 * Behavior:
 * 1. If authStatus === 'initializing' → show minimal skeleton (max 1.5s)
 * 2. If after 1.5s still initializing → attempt one session refresh
 * 3. If authStatus === 'unauthenticated' → immediately redirect to /login
 * 4. If authStatus === 'authenticated' → render children
 * 5. If authStatus === 'error' → attempt recovery, then redirect if fails
 */
const InstantProtectedRoute: React.FC<InstantProtectedRouteProps> = ({ 
  children,
  requireAdmin = false 
}) => {
  const { user, authStatus, checkSession } = useAuth();
  const { forceHide } = useGlobalLoadingControl();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const retryAttemptedRef = useRef(false);
  const [showSkeleton, setShowSkeleton] = useState(true);
  const redirectExecutedRef = useRef(false);

  // Handle initializing state with timeout
  useEffect(() => {
    if (authStatus === 'initializing') {
      setShowSkeleton(true);
      retryAttemptedRef.current = false;

      // Set timeout for recovery attempt
      timeoutRef.current = setTimeout(async () => {
        if (authStatus === 'initializing' && !retryAttemptedRef.current) {
          console.log('[InstantProtectedRoute] Initialization timeout, attempting session refresh...');
          retryAttemptedRef.current = true;

          try {
            // Attempt one session refresh
            const result = await checkSession();
            
            if (!result.isValid) {
              // Session invalid, redirect will be handled by unauthenticated check
              console.log('[InstantProtectedRoute] Session refresh failed, redirecting...');
            }
          } catch (error) {
            console.error('[InstantProtectedRoute] Error during session refresh:', error);
            // Will fall through to unauthenticated check
          }
        }
      }, 1500); // 1.5s timeout

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    } else {
      // Clear timeout if status changes
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setShowSkeleton(false);
    }
  }, [authStatus, checkSession]);

  // Handle unauthenticated state - immediate redirect
  useEffect(() => {
    if (authStatus === 'unauthenticated' && !redirectExecutedRef.current) {
      redirectExecutedRef.current = true;
      console.log('[InstantProtectedRoute] User not authenticated, redirecting to login...');
      
      // Force hide any loading indicators
      forceHide();
      
      // Safe redirect with state preservation
      const currentPath = window.location.pathname + window.location.search;
      safeRedirectToLogin(currentPath);
    }
  }, [authStatus, forceHide]);

  // Handle error state - attempt recovery then redirect
  useEffect(() => {
    if (authStatus === 'error' && !redirectExecutedRef.current) {
      console.warn('[InstantProtectedRoute] Auth error, attempting recovery...');
      
      // Attempt recovery
      checkSession().then(result => {
        if (!result.isValid) {
          redirectExecutedRef.current = true;
          forceHide();
          const currentPath = window.location.pathname + window.location.search;
          safeRedirectToLogin(currentPath);
        }
      }).catch(() => {
        redirectExecutedRef.current = true;
        forceHide();
        const currentPath = window.location.pathname + window.location.search;
        safeRedirectToLogin(currentPath);
      });
    }
  }, [authStatus, checkSession, forceHide]);

  // Check admin requirement
  useEffect(() => {
    if (requireAdmin && user) {
      const userRole = user.user_metadata?.role || getCachedUserMeta()?.role;
      if (userRole !== 'admin') {
        console.warn('[InstantProtectedRoute] Admin required but user is not admin');
        // Redirect to dashboard or show unauthorized message
        window.location.href = '/dashboard';
      }
    }
  }, [requireAdmin, user]);

  // Show skeleton while initializing (max 1.5s)
  if (authStatus === 'initializing' && showSkeleton) {
    return (
      <div className="min-h-screen bg-[#111726] flex items-center justify-center">
        <div className="text-center">
          {/* Compact skeleton instead of full-page spinner */}
          <div className="w-16 h-16 bg-gradient-to-br from-[#9c68f2] to-[#422ca5] rounded-xl mx-auto mb-4 flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          </div>
          <p className="text-[rgb(var(--muted))] text-sm">Checking session...</p>
        </div>
      </div>
    );
  }

  // Don't render anything if redirecting
  if (authStatus === 'unauthenticated' || redirectExecutedRef.current) {
    return null;
  }

  // Render children if authenticated
  if (authStatus === 'authenticated' && user) {
    // Verify admin requirement if needed
    if (requireAdmin) {
      const userRole = user.user_metadata?.role || getCachedUserMeta()?.role;
      if (userRole !== 'admin') {
        return null; // Will redirect
      }
    }
    
    return <>{children}</>;
  }

  // Fallback: show skeleton if in unexpected state
  return (
    <div className="min-h-screen bg-[#111726] flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 bg-gradient-to-br from-[#9c68f2] to-[#422ca5] rounded-xl mx-auto mb-4 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        </div>
        <p className="text-[rgb(var(--muted))] text-sm">Checking session...</p>
      </div>
    </div>
  );
};

export default InstantProtectedRoute;
